import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type CollectionReference,
} from 'firebase/firestore';
import type { Commit } from '@/pages/developer/types';
import { normalizeBillingAmount } from '@/pages/developer/githubUtils';

type DataSource = 'firestore' | 'local' | 'github';

type FirestoreCommitPagingState = {
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  loadedCount: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
};

export const useFirestoreCommitPaging = (args: {
  firestoreCommitsCollectionRef: CollectionReference<DocumentData> | null;
  ratePerCommit: number;
  dataSource: DataSource;
  commitsLength: number;
  firestoreCommitCount: number | null;
  pageSize: number;
  queryLimit: number;
  setCommits: (next: Commit[] | ((prev: Commit[]) => Commit[])) => void;
  setDataSource: (next: DataSource) => void;
}) => {
  const [state, setState] = useState<FirestoreCommitPagingState>({
    cursor: null,
    loadedCount: 0,
    hasMore: false,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (args.dataSource !== 'firestore') return;
    setState((prev) => {
      if (prev.loadedCount === args.commitsLength) return prev;
      return { ...prev, loadedCount: args.commitsLength };
    });
  }, [args.commitsLength, args.dataSource]);

  const mapDocToCommit = useCallback(
    (docSnap: QueryDocumentSnapshot<DocumentData>): Commit => {
      const data = docSnap.data() as Partial<Commit> & { syncedAt?: string };
      const base: Commit = {
        sha: docSnap.id,
        message: data.message ?? 'Untitled commit',
        author: data.author ?? 'Unknown',
        date: data.date ?? new Date().toISOString(),
        url: data.url ?? '',
        billed: data.billed ?? false,
        billingAmount: normalizeBillingAmount(data.billingAmount, args.ratePerCommit),
      };

      if (typeof data.additions === 'number') base.additions = data.additions;
      if (typeof data.deletions === 'number') base.deletions = data.deletions;
      if (typeof data.filesChanged === 'number') base.filesChanged = data.filesChanged;

      return base;
    },
    [args.ratePerCommit]
  );

  const loadCommitPageFromFirestore = useCallback(
    async (payload: { after: QueryDocumentSnapshot<DocumentData> | null; pageSize: number }) => {
      if (!args.firestoreCommitsCollectionRef) {
        return { commits: [] as Commit[], cursor: null as QueryDocumentSnapshot<DocumentData> | null };
      }

      const constraints: QueryConstraint[] = [orderBy('committedAt', 'desc'), limit(payload.pageSize)];
      if (payload.after) constraints.push(startAfter(payload.after));

      const snapshot = await getDocs(query(args.firestoreCommitsCollectionRef, ...constraints));
      if (snapshot.empty) {
        return { commits: [] as Commit[], cursor: payload.after };
      }

      const pageCommits = snapshot.docs.map(mapDocToCommit);
      const cursor = snapshot.docs[snapshot.docs.length - 1] ?? payload.after;
      return { commits: pageCommits, cursor };
    },
    [args.firestoreCommitsCollectionRef, mapDocToCommit]
  );

  const loadCommitsFromFirestore = useCallback(async () => {
    if (!args.firestoreCommitsCollectionRef) return null;

    const snapshot = await getDocs(
      query(args.firestoreCommitsCollectionRef, orderBy('committedAt', 'desc'), limit(args.queryLimit))
    );
    if (snapshot.empty) return [];

    return snapshot.docs.map(mapDocToCommit);
  }, [args.firestoreCommitsCollectionRef, args.queryLimit, mapDocToCommit]);

  const resetFirestoreCommitPaging = useCallback(() => {
    setState((prev) => ({ ...prev, cursor: null, loadedCount: 0, hasMore: false, error: null }));
  }, []);

  const loadInitialFirestoreCommits = useCallback(async () => {
    resetFirestoreCommitPaging();
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const page = await loadCommitPageFromFirestore({ after: null, pageSize: args.pageSize });
      args.setCommits(page.commits);
      setState((prev) => ({
        ...prev,
        cursor: page.cursor,
        hasMore: page.commits.length >= args.pageSize,
      }));
      args.setDataSource('firestore');
      return page.commits;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load commits from Firestore.';
      setState((prev) => ({ ...prev, error: message }));
      return null;
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [args, loadCommitPageFromFirestore, resetFirestoreCommitPaging]);

  const canLoadMoreFirestoreCommits = useMemo(() => {
    if (!args.firestoreCommitsCollectionRef) return false;
    if (args.dataSource !== 'firestore') return false;
    if (state.loading) return false;
    if (typeof args.firestoreCommitCount === 'number') {
      return state.loadedCount < args.firestoreCommitCount;
    }
    return state.hasMore;
  }, [args.dataSource, args.firestoreCommitCount, args.firestoreCommitsCollectionRef, state.hasMore, state.loadedCount, state.loading]);

  const loadMoreFirestoreCommits = useCallback(async () => {
    if (!args.firestoreCommitsCollectionRef) return false;
    if (state.loading) return false;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const page = await loadCommitPageFromFirestore({ after: state.cursor, pageSize: args.pageSize });
      if (page.commits.length === 0) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return false;
      }

      args.setCommits((prev) => {
        const bySha = new Map(prev.map((commit) => [commit.sha, commit] as const));
        page.commits.forEach((commit) => {
          if (!bySha.has(commit.sha)) bySha.set(commit.sha, commit);
        });
        return Array.from(bySha.values());
      });

      setState((prev) => ({
        ...prev,
        cursor: page.cursor,
        hasMore: page.commits.length >= args.pageSize,
      }));
      args.setDataSource('firestore');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load more commits.';
      setState((prev) => ({ ...prev, error: message }));
      return false;
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [args, loadCommitPageFromFirestore, state.cursor, state.loading]);

  return {
    firestoreCommitsCursor: state.cursor,
    firestoreCommitsLoadedCount: state.loadedCount,
    firestoreCommitsHasMore: state.hasMore,
    firestoreCommitsPagingLoading: state.loading,
    firestoreCommitsPagingError: state.error,
    resetFirestoreCommitPaging,
    loadInitialFirestoreCommits,
    canLoadMoreFirestoreCommits,
    loadMoreFirestoreCommits,
    loadCommitsFromFirestore,
  };
};
